import type { HeadlineKey } from './types.js'

/** Wording shared by the Insights screens. */

export const FEATURE_KEY = 'executive_insights'
export const FEATURE_NAME = 'Executive Insights'
export const PAGE_SEGMENT = 'insights'

export const HEADLINE_LABELS: Record<HeadlineKey, string> = {
  score: 'Average score',
  goldPct: 'Gold share',
  callOutsPer100: 'Call-outs / 100 shifts',
  fillPct: 'Fill from roster',
  hppdWorked: 'HPPD worked',
  retentionPct: 'Retention',
  headcount: 'Workers',
}

/** The figures a portfolio row leads with, in the order the table shows them. */
export const TABLE_HEADLINES: readonly HeadlineKey[] = ['score', 'goldPct', 'callOutsPer100', 'fillPct', 'hppdWorked', 'retentionPct']

/** A figure where a fall is the good direction. */
export const LOWER_IS_BETTER: Readonly<Record<HeadlineKey, boolean>> = {
  score: false,
  goldPct: false,
  callOutsPer100: true,
  fillPct: false,
  hppdWorked: false,
  retentionPct: false,
  headcount: false,
}

/** How a figure is written: a share, a rate, hours or a plain number. */
export function formatHeadline(key: HeadlineKey, value: number | null): string {
  if (value === null) {
    return '—'
  }
  switch (key) {
    case 'goldPct':
    case 'fillPct':
    case 'retentionPct':
      return `${Math.round(value)}%`
    case 'hppdWorked':
      return value.toFixed(2)
    case 'callOutsPer100':
    case 'score':
      return value.toFixed(1)
    default:
      return String(value)
  }
}

/** A change since the month before, signed, in the figure's own unit. */
export function formatMovement(key: HeadlineKey, value: number | null): string {
  if (value === null) {
    return '—'
  }
  const sign = value > 0 ? '+' : ''
  switch (key) {
    case 'goldPct':
    case 'fillPct':
    case 'retentionPct':
      return `${sign}${Math.round(value)} pt`
    case 'hppdWorked':
      return `${sign}${value.toFixed(2)}`
    case 'headcount':
      return `${sign}${Math.round(value)}`
    default:
      return `${sign}${value.toFixed(1)}`
  }
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_NAMES_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** `2026-10` as "Oct 26", or "October 2026" in full. */
export function monthLabel(period: string, style: 'short' | 'long' = 'short'): string {
  const [year = '', month = ''] = period.split('-')
  const index = Number(month) - 1
  return style === 'short' ? `${MONTH_NAMES[index] ?? month} ${year.slice(2)}` : `${MONTH_NAMES_LONG[index] ?? month} ${year}`
}

/** An ISO instant as "3 Oct 2026". */
export function dayLabel(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso.slice(0, 10) : `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

export const BEHAVIOR_LABELS: Readonly<Record<string, string>> = {
  call_out_rate: 'Call-out rate',
  on_time_arrival: 'On-time arrival',
  shift_completion: 'Shift completion',
  no_show_rate: 'No-show rate',
  charting_completion: 'Charting completion',
}

export const MARKER_AREA_LABELS: Readonly<Record<string, string>> = {
  facility_config: 'facility settings',
  behaviors: 'behaviors',
  add_on_license: 'a license',
  add_on_switch: 'an add-on switch',
}
